import React, { useRef, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TableContainer from '@mui/material/TableContainer';

import { Iconify } from 'src/components/iconify';

import { markdownClasses } from './classes';

// ----------------------------------------------------------------------
// Custom table components for Markdown: render tables with MUI Table for
// better readability (spacing, borders, sticky header, horizontal scroll),
// plus an optional \"Expand\" dialog for wide / dense tables.
// ----------------------------------------------------------------------

/**
 * Build CSV string from a table element (all rows: header + body).
 * Uses thead + tbody explicitly so all data is included even when some rows are hidden by CSS.
 */
function tableToCsv(tableEl) {
  const csvRows = [];
  const thead = tableEl.querySelector('thead');
  const tbody = tableEl.querySelector('tbody');
  const collectRow = (tr) => {
    const cells = tr.querySelectorAll('th, td');
    return Array.from(cells).map((cell) => {
      const text = (cell.textContent ?? '').replace(/\s+/g, ' ').trim();
      return `"${text.replace(/"/g, '""')}"`;
    });
  };
  if (thead) {
    thead.querySelectorAll('tr').forEach((tr) => csvRows.push(collectRow(tr).join(',')));
  }
  if (tbody) {
    tbody.querySelectorAll('tr').forEach((tr) => csvRows.push(collectRow(tr).join(',')));
  }
  return csvRows.join('\n');
}

const INLINE_ROW_LIMIT = 5;

function getBodyRowCount(children) {
  const arr = React.Children.toArray(children);
  const tbody = arr[1];
  return tbody ? React.Children.count(tbody.props?.children ?? []) : 0;
}

function MarkdownTableWrapper({ children, compact = false }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const rowCount = getBodyRowCount(children);
  const hasMoreRows = rowCount > INLINE_ROW_LIMIT;

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleDownload = useCallback(() => {
    const table = containerRef.current?.querySelector('table');
    if (!table) return;
    const csv = tableToCsv(table);
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `table-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  return (
    <>
      <Box ref={containerRef} sx={{ position: 'relative', mb: 2 }}>

        <TableContainer
          className={markdownClasses.content.table}
          sx={{
            ...(compact && { display: 'none' }),
            borderRadius: 1.5,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: (theme) => theme.shadows[1],
            overflowX: 'hidden',
            overflowY: 'auto',
            maxHeight: { xs: 360, sm: 480 },
            '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(n+6)': {
              display: 'none',
            },
          }}
        >
          <Table size="small" stickyHeader>
            {children}
          </Table>
        </TableContainer>

        {!compact && hasMoreRows && (
          <Typography
            data-print-hide="true"
            variant="caption"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              px: 1,
              color: 'text.secondary',
            }}
          >
            <Iconify icon="solar:info-circle-bold" width={16} />
            Showing first {INLINE_ROW_LIMIT} of {rowCount} rows. Use Expand to see all.
          </Typography>
        )}
      </Box>

      <Stack data-print-hide="true" direction="row" spacing={1} justifyContent="flex-end">
        <Button
          size="small"
          variant="outlined"
          onClick={handleDownload}
          startIcon={<Iconify icon="solar:download-minimalistic-bold" width={18} />}
          sx={{ textTransform: 'none', px: 1, py: 0.25 }}
        >
          Download
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleOpen}
          startIcon={<Iconify icon="solar:maximize-square-bold" width={18} />}
          sx={{ textTransform: 'none', px: 1, py: 0.25 }}
        >
          Expand
        </Button>
      </Stack>

      <Dialog
        fullWidth
        maxWidth="lg"
        open={open}
        onClose={handleClose}
        aria-labelledby="markdown-table-dialog-title"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              boxShadow: (theme) => theme.shadows[24],
              maxHeight: '90vh',
            },
          },
        }}
      >
        <DialogTitle
          id="markdown-table-dialog-title"
          sx={{
            py: 2,
            pr: 6,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <IconButton
              aria-label="Close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 12,
              }}
            >
              <Iconify icon="solar:close-circle-bold" width={24} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: 'background.neutral' }}>
          <Box sx={{ p: 2 }}>
            <TableContainer
              className={markdownClasses.content.table}
              sx={{
                maxHeight: '70vh',
                overflow: 'auto',
                borderRadius: 1.5,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                boxShadow: (theme) => theme.shadows[1],
              }}
            >
              <Table size="small" stickyHeader>
                {children}
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const markdownTableComponents = {
  table: MarkdownTableWrapper,
  thead: ({ node, children, ...other }) => <TableHead>{children}</TableHead>,
  tbody: ({ node, children, ...other }) => <TableBody>{children}</TableBody>,
  tr: ({ node, children, ...other }) => <TableRow>{children}</TableRow>,
  th: ({ node, children, ...other }) => (
    <TableCell
      variant="head"
      sx={{
        fontWeight: 600,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderTop: 0,
        '&:first-of-type': { borderLeft: 0 },
        '&:last-of-type': { borderRight: 0 },
      }}
    >
      {children}
    </TableCell>
  ),
  td: ({ node, children, ...other }) => (
    <TableCell
      sx={{
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}
    >
      {children}
    </TableCell>
  ),
};
